import { Pill } from "@/components/ui/pill";
import {
  METRIC_LABELS,
  PERCENT_METRICS,
  type AppliedScenarioMetricKey,
  type SettlementMetric,
} from "@/types/applied-scenario.types";

interface SettlementColumnsProps {
  metrics: SettlementMetric[];
}

// Previsto × realizado × base, com o desvio contra CADA uma das
// duas referências.
// Duas regras que a tela não pode relaxar:
//  1. Nulo vira "—", NUNCA `0`. Uma previsão sem plano é nula; desenhá-la como zero
//     diria "o gêmeo previu produção péssima" quando ele não previu nada.
//  2. O veredito é rotulado como TOLERÂNCIA, jamais como acurácia. Acurácia depende de
//     aderência ao plano — se a fábrica sequer executou o que foi comprometido — e isso ainda não
//     existe. Chamar de acurácia transformaria "declarei uma faixa larga" em "o gêmeo acerta".

/** Nulo é informação. "—" é como a plataforma inteira desenha KPI ausente. */
function fmt(value: number | null, metric: AppliedScenarioMetricKey): string {
  if (value === null) return "—";
  const suffix = PERCENT_METRICS.has(metric) ? "%" : "";
  return `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}${suffix}`;
}

function delta(value: number | null): { text: string; tone: "success" | "danger" | "neutral" } {
  if (value === null) return { text: "—", tone: "neutral" };
  if (value === 0) return { text: "0", tone: "neutral" };
  const sign = value > 0 ? "+" : "";
  return {
    text: `${sign}${value.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}`,
    tone: value > 0 ? "success" : "danger",
  };
}

export function SettlementColumns({ metrics }: SettlementColumnsProps) {
  return (
    <div className="space-y-2">
      <h3 className="text-[13px] font-medium">Apuração</h3>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="border-b border-border-subtle text-left text-[11px] uppercase tracking-wide text-text-tertiary">
              <th className="py-1.5 pr-3 font-medium">Métrica</th>
              <th className="py-1.5 pr-3 text-right font-medium">Previsto</th>
              <th className="py-1.5 pr-3 text-right font-medium">Realizado</th>
              <th className="py-1.5 pr-3 text-right font-medium">Não fazer nada</th>
              <th className="py-1.5 pr-3 text-right font-medium">vs. previsto</th>
              <th className="py-1.5 pr-3 text-right font-medium">vs. base</th>
              <th className="py-1.5 text-right font-medium">Tolerância</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {metrics.map((m) => {
              const vsPredicted = delta(m.deltaVsPredicted);
              const vsBaseline = delta(m.deltaVsBaseline);
              return (
                <tr key={m.metric}>
                  <td className="py-1.5 pr-3">{METRIC_LABELS[m.metric]}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-text-secondary">
                    {fmt(m.predicted, m.metric)}
                  </td>
                  <td className="py-1.5 pr-3 text-right font-medium tabular-nums">
                    {fmt(m.realized, m.metric)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-text-secondary">
                    {fmt(m.baseline, m.metric)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    <Pill tone={vsPredicted.tone}>{vsPredicted.text}</Pill>
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    <Pill tone={vsBaseline.tone}>{vsBaseline.text}</Pill>
                  </td>
                  <td className="py-1.5 text-right">
                    {m.withinTolerance === null ? (
                      <span className="text-text-tertiary">—</span>
                    ) : (
                      <Pill tone={m.withinTolerance ? "success" : "warning"} withDot>
                        {m.withinTolerance ? "Na faixa" : "Fora da faixa"}
                      </Pill>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[12px] text-text-secondary">
        &ldquo;Na faixa&rdquo; significa que o desvio ficou dentro da tolerância{" "}
        <strong className="font-medium text-text-primary">declarada na aplicação</strong> — não é
        uma medida de acurácia do gêmeo. Métrica sem faixa declarada fica sem veredito.
      </p>
    </div>
  );
}
